import { createSlice } from '@reduxjs/toolkit';
import {
  request,
  generateCancelToken,
  cancelRequests,
  isCancel,
  ejectCancelInterceptor,
} from 'modules/Api/HttpClient';
import {
  USER_TEACHERS_ACTIVATE_URL,
  USER_TEACHERS_URL,
  USER_TEACHERS_CREATE_URL,
  USER_TEACHERS_UPDATE_URL,
  USER_TEACHERS_DELETE_URL,
  USER_TEACHER_BY_ID_URL,
  SETTINGS_REMOVE_AVATAR_URL,
  GROUPS_URL,
  LANGUAGES_URL,
  TIMEZONES_URL,
  UNITS_BY_GROUP_URL,
  SCHOOLS_BY_UNIT_URL,
  CLASSES_BY_SCHOOL_URL,
} from 'modules/Api/Routes';
import { formDataFromObj } from 'modules/Api/RequestData';
import { defaultRequest } from 'modules/Utils/DefaultRequest';
import { getUser } from 'modules/Auth/Services';

let cancelToken;

const initialState = {
  loading: false,
  error: null,
  data: {
    page: 1,
    perPage: 10,
    groupId: '',
    unitId: '',
    schoolId: '',
    classId: '',
    sort: {
      sortType: null,
      sortBy: null,
    },
  },
  languages: {
    data: [],
    loading: false,
  },
  timezones: {
    data: [],
    loading: false,
  },
  groups: {
    data: [],
    loading: false,
  },
  units: {
    data: [],
    empty: false,
    loading: false,
  },
  schools: {
    data: [],
    empty: false,
    loading: false,
  },
  classes: {
    data: [],
    empty: false,
    loading: false,
  },
  isDeleting: false,
};

const teachersSlice = createSlice({
  name: 'teachers',
  initialState,
  reducers: {
    cancelRequests: () => {
      cancelToken?.cancel();
      cancelRequests();
    },
    cleanState: () => ({ ...initialState }),
    /**
     * indicate that a request is started
     */
    requestTeachers: (state) => {
      state.loading = true;
      state.error = null;
    },
    setLoading: (state, action) => {
      state.loading = action.payload;
    },
    setIsDeleting: (state, action) => {
      state.isDeleting = action.payload;
    },
    changePerPage: (state, action) => {
      state.data.perPage = action.payload;
      state.data.page = 1;
    },
    changeSort: (state, action) => {
      const newSort = action.payload;
      state.data.sort.sortType =
        state.data.sort.sortBy === newSort && state.data.sort.sortType === 'ASC'
          ? 'DESC'
          : 'ASC';
      state.data.sort.sortBy = action.payload;
    },
    requestLanguages: (state) => {
      state.languages = {
        ...state.languages,
        loading: true,
      };
    },
    requestGroups: (state) => {
      state.groups = {
        ...state.groups,
        loading: true,
      };
    },
    requestTimezones: (state) => {
      state.timezones = {
        ...state.timezones,
        loading: true,
      };
    },
    receiveTimezonesList: (state, action) => {
      state.timezones = {
        ...state.timezones,
        loading: false,
        data: action.payload.content,
      };
    },
    receiveLanguagesError: (state, action) => {
      state.languages = {
        ...state.languages,
        loading: false,
      };
      state.error = action.payload;
    },
    receiveTimezonesError: (state, action) => {
      state.timezones = {
        ...state.timezones,
        loading: false,
      };
      state.error = action.payload;
    },
    receiveUnitsError: (state, action) => {
      state.units = {
        ...state.units,
        loading: false,
      };
      state.error = action.payload;
    },
    receiveSchoolError: (state, action) => {
      state.schools = {
        ...state.schools,
        loading: false,
      };
      state.error = action.payload;
    },
    receiveClassesError: (state, action) => {
      state.classes = {
        ...state.classes,
        loading: false,
      };
      state.error = action.payload;
    },
    receiveGroupsError: (state, action) => {
      state.groups = {
        ...state.groups,
        loading: false,
      };
      state.error = action.payload;
    },
    /**
     * receive a success response
     */
    receiveRequestSuccess: (state) => {
      state.lading = false;
    },
    /**
     * receive a success teachers list response
     */
    receiveTeachersList: (state, action) => {
      state.loading = false;
      state.data = {
        ...state.data,
        teachers: action.payload.users,
        total: action.payload.total_items,
      };
    },
    clearTeachersList: (state) => {
      state.loading = false;
      state.data = {
        page: 1,
        perPage: 10,
        groupId: '',
        unitId: '',
        schoolId: '',
        classId: '',
        sort: {
          sortType: null,
          sortBy: null,
        },
      };
    },
    /**
     * receive a success language list response
     */
    receiveLanguagesList: (state, action) => {
      state.languages = {
        ...state.languages,
        loading: false,
        data: action.payload.content,
      };
    },
    receiveGroupsList: (state, action) => {
      state.groups.loading = false;

      if (action.payload.content.groups.length) {
        state.groups = {
          ...state.groups,
          data: action.payload.content.groups,
        };
        return;
      }

      const loggedUser = getUser();
      state.groups = {
        ...state.groups,
        data: loggedUser?.group ? [loggedUser?.group] : [],
      };
    },
    /**
     * receive an error response
     */
    receiveTeachersError: (state, action) => {
      state.loading = false;
      state.error = action.payload;
    },
    changeTeachersPage: (state, action) => {
      state.data.page = action.payload;
    },
    requestUnits: (state) => {
      state.units = {
        ...state.units,
        loading: true,
      };
    },
    receiveUnitsList: (state, action) => {
      state.units.loading = false;

      if (action.payload.content?.length) {
        state.units = {
          ...state.units,
          empty: !action.payload.content?.length,
          data: action.payload.content,
        };
        return;
      }

      const loggedUser = getUser();
      state.units = {
        ...state.units,
        empty: !loggedUser?.unit,
        data: loggedUser?.unit ? [loggedUser?.unit] : [],
      };
    },
    requestSchool: (state) => {
      state.schools = {
        ...state.schools,
        loading: true,
      };
    },
    receiveSchoolList: (state, action) => {
      state.schools.loading = false;

      if (action.payload.content.schools.length) {
        state.schools = {
          ...state.schools,
          empty: !action.payload.content?.schools?.length,
          data: action.payload.content.schools,
        };
        return;
      }

      const loggedUser = getUser();
      state.schools = {
        ...state.schools,
        empty: !loggedUser?.school,
        data: loggedUser?.school ? [loggedUser?.school] : [],
      };
    },
    requestClasses: (state) => {
      state.classes = {
        ...state.classes,
        loading: true,
      };
    },
    receiveClassesList: (state, action) => {
      state.classes = {
        ...state.classes,
        loading: false,
        empty: !action.payload.content.length,
        data: action.payload.content,
      };
    },
    clearFormState: (state) => {
      state.units = {
        data: [],
        empty: false,
        loading: false,
      };
      state.schools = {
        data: [],
        empty: false,
        loading: false,
      };
      state.classes = {
        data: [],
        empty: false,
        loading: false,
      };
    },
    clearSchoolsAndClasses: (state) => {
      state.schools = {
        data: [],
        empty: false,
        loading: false,
      };
      state.classes = {
        data: [],
        empty: false,
        loading: false,
      };
    },
    changeTeacherAdminSearch: (state, action) => {
      state.data.groupId = action.payload.group_id;
      state.data.unitId = action.payload.unit_id;
      state.data.schoolId = action.payload.school_id;
      state.data.classId = action.payload.class_id;
      state.data.search = action.payload.search;
      state.data.page = 1;
    },
    changeTeacherFilters: (state, action) => {
      state.data.groupId = action.payload?.group_id || '';
      state.data.unitId = action.payload?.unit_id || '';
      state.data.schoolId = action.payload?.school_id || '';
      state.data.classId = action.payload?.class_id || '';
      state.data.page = isNaN(action?.payload?.page)
        ? 1
        : Number(action?.payload?.page);
      state.data.perPage = isNaN(action?.payload?.paginates_per)
        ? state.data.perPage
        : Number(action?.payload?.paginates_per);
      state.data.search = action?.payload.search || '';
    },
    clearTeachersFilters: (state) => {
      state.data.groupId = null;
      state.data.unitId = null;
      state.data.schoolId = null;
      state.data.classId = null;
      state.data.search = '';
      state.data.page = 1;
      state.data.perPage = 10;
      state.data.sort.sortType = null;
      state.data.sort.sortBy = null;
    },
  },
});

const Actions = teachersSlice.actions;

const Selectors = {
  fetchListData: (state) => state.teachers,
  teacherLoading: ({ teachers: { loading } }) => ({ loading }),
};

const Async = {
  setActivated:
    ({ data: teachers, activated, onSuccess, onError }) =>
    async () => {
      try {
        const data = new FormData();
        data.set('id', teachers.id);
        data.set('activate', activated);

        const response = await request({
          method: 'PUT',
          url: USER_TEACHERS_ACTIVATE_URL,
          data,
        });

        onSuccess(response);
      } catch (e) {
        onError(e);
      }
    },

  fetchTeachersList: () => async (dispatch, getState) => {
    const {
      teachers: {
        data: {
          page,
          perPage,
          search,
          groupId,
          unitId,
          schoolId,
          classId,
          sort: { sortType, sortBy },
        },
      },
    } = getState();

    ejectCancelInterceptor();
    cancelToken?.cancel();
    cancelToken = generateCancelToken();

    let action;

    dispatch(Actions.requestTeachers());

    try {
      const response = await request({
        cancelToken: cancelToken.token,
        method: 'GET',
        url: USER_TEACHERS_URL,
        params: {
          page,
          paginates_per: perPage,
          search,
          sort: sortType,
          sort_by: sortBy,
          group_id: groupId,
          unit_id: unitId,
          school_id: schoolId,
          class_id: classId,
        },
      });

      action = Actions.receiveTeachersList(response.data.content);
    } catch (e) {
      if (!isCancel(e)) {
        action = Actions.receiveTeachersError(e.message);
      }
    }

    action && dispatch(action);
  },

  createTeacher:
    ({ data, onSuccess, onError }) =>
    async (dispatch) => {
      dispatch(Actions.requestTeachers());

      try {
        if (!data.avatar) delete data.avatar;
        const teachersData = formDataFromObj(data);

        const response = await request({
          method: 'POST',
          url: USER_TEACHERS_CREATE_URL,
          data: teachersData,
        });

        dispatch(Actions.receiveRequestSuccess());
        onSuccess(response);
      } catch (e) {
        dispatch(Actions.receiveTeachersError());
        onError(e);
      }
    },

  getTeacherById:
    ({ id, onSuccess, onError }) =>
    async () => {
      try {
        const response = await request({
          method: 'GET',
          url: `${USER_TEACHER_BY_ID_URL}?id=${id}`,
        });

        onSuccess(response);
      } catch (e) {
        onError(e);
      }
    },

  deleteTeacher:
    ({ id, onSuccess, onError }) =>
    async (dispatch) => {
      dispatch(Actions.setIsDeleting(true));

      try {
        const response = await request({
          method: 'DELETE',
          url: `${USER_TEACHERS_DELETE_URL}?id=${id}`,
        });

        onSuccess(response);
        dispatch(Actions.setIsDeleting(false));
      } catch (e) {
        onError(e);
        dispatch(Actions.setIsDeleting(false));
      }
    },

  updateTeacher:
    ({ data, onSuccess, onError }) =>
    async (dispatch) => {
      dispatch(Actions.requestTeachers());
      try {
        if (!(data.avatar instanceof File)) delete data.avatar;
        const teachersData = formDataFromObj(data);

        const response = await request({
          method: 'PUT',
          url: USER_TEACHERS_UPDATE_URL,
          data: teachersData,
        });

        dispatch(Actions.receiveRequestSuccess());
        onSuccess(response);
      } catch (e) {
        dispatch(Actions.receiveTeachersError());
        onError(e);
      }
    },

  removeAvatar:
    ({ id, onSuccess, onError }) =>
    async (dispatch) => {
      try {
        const data = formDataFromObj({ id });

        const response = await request({
          method: 'PUT',
          url: SETTINGS_REMOVE_AVATAR_URL,
          data,
        });
        dispatch(Actions.receiveRequestSuccess());
        onSuccess(response);
      } catch (e) {
        onError(e);
      }
    },

  fetchGroupsList: () => async (dispatch) =>
    defaultRequest(
      dispatch,
      `${GROUPS_URL}?page=1&paginates_per=1000`,
      'GET',
      Actions.requestGroups,
      Actions.receiveGroupsList,
      Actions.receiveGroupsError
    ),

  fetchLanguagesList: () => async (dispatch) =>
    defaultRequest(
      dispatch,
      LANGUAGES_URL,
      'GET',
      Actions.requestLanguages,
      Actions.receiveLanguagesList,
      Actions.receiveLanguagesError
    ),

  fetchTimezonesList: () => async (dispatch) =>
    defaultRequest(
      dispatch,
      TIMEZONES_URL,
      'GET',
      Actions.requestTimezones,
      Actions.receiveTimezonesList,
      Actions.receiveTimezonesError
    ),

  fetchUnitsList: (groupId) => async (dispatch) =>
    defaultRequest(
      dispatch,
      `${UNITS_BY_GROUP_URL}?group_id=${groupId}`,
      'GET',
      Actions.requestUnits,
      Actions.receiveUnitsList,
      Actions.receiveUnitsError
    ),

  fetchSchoolList: (unitId) => async (dispatch) =>
    defaultRequest(
      dispatch,
      `${SCHOOLS_BY_UNIT_URL}?unit_id=${unitId}`,
      'GET',
      Actions.requestSchool,
      Actions.receiveSchoolList,
      Actions.receiveSchoolError
    ),

  fetchClassesList: (schoolId) => async (dispatch) =>
    defaultRequest(
      dispatch,
      `${CLASSES_BY_SCHOOL_URL}?school_id=${schoolId}`,
      'GET',
      Actions.requestClasses,
      Actions.receiveClassesList,
      Actions.receiveClassesError
    ),
};

const { reducer } = teachersSlice;

export { reducer, Actions, Async, Selectors };
